/* @flow */
import * as React from "react";
import prefixStyles, { type StylesObject } from "../utils/prefixStyles";
import { withColorsProvider, type ColorSet } from "../ColorsProvider";
import Toggle from "../Toggle";
import Checkbox from "../Checkbox";
import Tappable from "../Tappable";
import Theme from "../Theme";
import NoStyleButton from "../NoStyleButton";

// A string for people
type HumanReadableString = string;
// A string for browsers
type HTMLString = string;

type BooleanItemLabelProps = {
  labelHasLink?: ?boolean,
  label: HumanReadableString | HTMLString,
  style?: ?StylesObject,
  onTap?: ?() => void,
  "aria-pressed"?: ?boolean,
  colors: ColorSet,
};

class BooleanItemLabelWithoutColor extends React.Component<BooleanItemLabelProps> {
  render() {
    const {
      labelHasLink,
      label,
      style,
      onTap,
      "aria-pressed": ariaPressed,
      colors,
    } = this.props;
    let styles = {
      labelWrapper: {
        maxWidth: "66%",
        flexGrow: "1",
        flexShrink: "0",
      },
      label: {
        color: colors.TEXT,
        width: "100%",
        textAlign: "left",
        ...Theme.Styles.flexStretch,
      },
    };

    return (
      <div style={{ ...styles.labelWrapper, ...style }}>
        {labelHasLink ? (
          <span
            data-component-section="label"
            style={styles.label}
            dangerouslySetInnerHTML={{ __html: label }}
          />
        ) : (
          <Tappable onTap={onTap}>
            {({ tapTargetRefHandler }) => (
              <NoStyleButton
                innerRef={tapTargetRefHandler}
                style={styles.label}
                aria-pressed={ariaPressed}
                data-component-section="label"
              >
                {label}
              </NoStyleButton>
            )}
          </Tappable>
        )}
      </div>
    );
  }
}

const BooleanItemLabel = withColorsProvider(BooleanItemLabelWithoutColor);

const booleanItemStyles = prefixStyles({
  checkboxItemContainer: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
  },
});

type Props = {
  appearance: "checkbox" | "toggle",
  value?: ?boolean,
  label: HumanReadableString | HTMLString,
  labelPositionRight?: ?boolean,
  labelHasLink?: ?boolean,
  onChange?: ?(newValue: boolean) => void,
  labelStyle?: Object,
};

export default class BooleanItem extends React.Component<Props> {
  static defaultProps = {
    appearance: "toggle",
  };

  _handleChecked = (newValue: boolean = !this.props.value) => {
    const { onChange } = this.props;

    if (onChange) {
      onChange(newValue);
    }
  };

  render() {
    const {
      label,
      labelPositionRight,
      labelHasLink,
      value,
      appearance,
      labelStyle,
    } = this.props;

    let labelNoHTML = label;
    if (labelHasLink) {
      // Ensure the aria label for the Toggle doesn't contain raw HTML.
      const div = document.createElement("div");
      div.innerHTML = label;
      labelNoHTML = div.textContent;
    }

    const booleanItemLabelProps = {
      labelHasLink,
      label,
      style: labelStyle,
      onTap: this._handleChecked,
      "aria-pressed": value,
    };

    const appearances = {
      toggle: (
        <Toggle
          checked={value}
          onChange={this._handleChecked}
          aria-label={labelNoHTML}
        />
      ),
      checkbox: (
        <Checkbox
          checked={value}
          onTap={this._handleChecked}
          aria-label={labelNoHTML}
        />
      ),
    };

    return (
      <div
        data-component-name="BooleanItem"
        style={{
          textAlign: "left",
          display: "block",
          width: "100%",
        }}
      >
        <div style={booleanItemStyles.checkboxItemContainer}>
          {!labelPositionRight ? (
            <BooleanItemLabel {...booleanItemLabelProps} />
          ) : null}
          {appearances[appearance] || appearances.toggle}
          {labelPositionRight ? (
            <BooleanItemLabel
              style={{
                paddingLeft: "1em",
                maxWidth: "90%",
                fontSize: "0.9em",
              }}
              {...booleanItemLabelProps}
            />
          ) : null}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ../nx-components/src/internal/BooleanItem.jsx